//# IMPORTS

// Asset imports
import styles from "./AIChat.module.css";
import IconSend from "../../assets/send.svg?react";

// Project imports
import { AIChatConversationView } from "../AIChatConversationView/AIChatConversationView";
import { SafeHTML } from "../SafeHTML/SafeHTML";
import { AIChatClientProps } from "../AIChatClient/AIChatClient";
import AIChatTitlebar from "../AIChatTitlebar/AIChatTitlebar";
import { RefreshButton } from "../RefreshButton/RefreshButton";
import {
	ApiAdapter,
	ApiAdapterMessage,
	ApiAdapterMessageRole,
	ApiAdapterThread,
} from "@/api-adapters/ApiAdapter";
import { JwtHandler } from "@/utilities/JwtHandler";
import {
	messageDeltaFilters,
	messageContentFilters,
	linkCitations,
} from "@/utilities/response-filters";
import { Links } from "@/utilities/types";

// import { OpenaiAssistantsApiAdapter } from '@/api-adapters/OpenaiAssistantsApiAdapter';
import { OpenaiResponsesApiAdapter } from "@/api-adapters/OpenaiResponsesApiAdapter";

// OpenAI imports
import {
	APIConnectionError,
	AuthenticationError,
	PermissionDeniedError,
	RateLimitError,
} from "openai/error";

// General imports
import { ReactElement, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import {
	ActionIcon,
	Box,
	Center,
	Flex,
	Group,
	Loader,
	Modal,
	Paper,
	ScrollArea,
	SimpleGrid,
	Space,
	Text,
	TextInput,
	Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import wpFetch from "@/utilities/wpFetch";

//# COMPONENT

interface AIChatProps extends AIChatClientProps {
	byColorScheme: any;
}

export function AIChat({
	assistantID,
	vectorStoreId,
	siteName,
	siteTopics,
	assistantPersonality,
	appTitle,
	appTitleImageURL,
	welcomeHTML,
	promptSuggestions,
	autolinkText,
	proxyURL,
	initialNonce,
	captchaRequired,
	captchaSiteKey,
	byColorScheme,
	utmSource,
	utmMedium,
	utmCampaign,
}: AIChatProps): ReactElement {
	const prefix = "justask";

	//# Hooks
	//# – State hooks
	const [apiReady, setApiReady] = useState<boolean>(false);
	const [userInput, setUserInput] = useState<string>("");
	const [messages, setMessages] = useState<ApiAdapterMessage[]>([]);
	const [messagePostQueue, setMessagePostQueue] = useState<number[]>([]);
	const [sendQueue, setSendQueue] = useState<string[]>([]);
	const [thread, setThread] = useState<string>("");
	const [statusText, setStatusText] = useState<string | null>("Initializing…");
	const [activeView, setActiveView] = useState<"wait" | "welcome" | "conversation">("wait");
	const [citationLinkCache, setCitationLinkCache] = useState<Links>({});
	const [captchaPending, setCaptchaPending] = useState(captchaRequired);
	const [captchaError, setCaptchaError] = useState(false);
	const [cachedJwtHandler, setCachedJwtHandler] = useState<JwtHandler | undefined>();
	const [apiAdapter, setApiAdapter] = useState<ApiAdapter | undefined>();

	//# – Misc. hooks
	const streamingMessage = {
		textRef: useRef<string>(""),
		indexRef: useRef<number | null>(null),
		componentRef: useRef<any>(null),
	};
	const [isStreaming, streamingSpinnerCtrl] = useDisclosure();
	const streamRef = useRef<any>(null);

	const [isSessionExpired, sessionExpiredCtrl] = useDisclosure(false);
	const [captchaInteractive, captchaInteractiveCtrl] = useDisclosure(false);
	const [cookies, setCookie, removeCookie] = useCookies([`${prefix}__thread`, `${prefix}__jwt`]);

	//# – Effect hooks

	//# –– Initialization sequence

	useEffect(() => {
		const initApiAdapter = async () => {
			// const adapter = new OpenaiAssistantsApiAdapter(getJwtHandler(), assistantID, proxyURL);
			const adapter = new OpenaiResponsesApiAdapter(
				getJwtHandler(),
				assistantID,
				vectorStoreId,
				siteName,
				siteTopics,
				assistantPersonality,
				proxyURL,
			);
			await adapter.init();
			setApiAdapter(adapter);
		};
		initApiAdapter();
	}, []); // Init

	useEffect(() => {
		if (typeof apiAdapter === "undefined") {
			console.log("Waiting for API adapter…");
			setApiReady(false);
			return;
		}

		setApiReady(true);
		console.log("Found API adapter.");

		// Show waiting for thread or welcome screen
		if (`${prefix}__thread` in cookies) {
			setActiveView("wait");
			setStatusText("Loading conversation…");

			if (!captchaPending) {
				fetchThread(cookies[`${prefix}__thread`]);
			}
		} else {
			setActiveView("welcome");
			setStatusText(null);
		}
	}, [apiAdapter]);

	//# –– Other effects

	useEffect(() => {
		if (activeView !== "conversation" && messages.length > 0) {
			if (activeView === "wait") {
				setStatusText(null);
			}

			setActiveView("conversation");
		}
	}, [messages]);

	useEffect(() => {
		const cookieOptions = {
			path: "/",
			maxAge: 12 * 60 * 60, // X * 60 * 60 = X hours
		};

		if (thread.length) {
			if (activeView === "wait") {
				setStatusText(null);
			}
			setActiveView("conversation");
			setCookie(`${prefix}__thread`, thread, cookieOptions);
		} else {
			if (apiReady) {
				removeCookie(`${prefix}__thread`, cookieOptions);
				setActiveView("welcome");
				setMessages([]);
			}
		}
	}, [thread]);

	useEffect(() => {
		if (sendQueue.length > 0 && apiReady) {
			setStatusText("");
			sendMessage(sendQueue[0])
				.catch((reason: any) => {
					console.error(`sendMessage catch: ${reason}`);
				})
				.finally(() => {
					setSendQueue((prevQueue) => prevQueue.slice(1));
				});
		}
	}, [sendQueue, apiReady]);

	useEffect(() => {
		if (messagePostQueue.length > 0) {
			const postProcessMessage = async () => {
				const messageIndex = messagePostQueue[0];
				let message = messages[messageIndex];

				message = await linkCitations(
					message,
					getJwtHandler().nonce,
					citationLinkCache,
					setCitationLinkCache,
					autolinkText,
				);
				message.content = messageContentFilters.apply(message.content as string);

				setMessages((prevMessages) => {
					const newMessages = [...prevMessages];
					newMessages[messageIndex] = message;
					return newMessages;
				});

				// remove number from array
				setMessagePostQueue((prevQueue) => prevQueue.slice(1));
			};

			postProcessMessage();
		}
	}, [messagePostQueue]);

	//# Functions

	const cleanupStreaming = () => {
		setStatusText(null);
		streamingSpinnerCtrl.close();
		streamingMessage.indexRef.current = null;
		streamingMessage.textRef.current = "";
		streamRef.current = null;
	};

	const showError = (message: string = "Sorry, something went wrong.") => {
		alert(
			`${message} Please try again. If this problem persists, please contact customer service.`,
		);
	};

	const getJwtHandler = () => {
		let handler = cachedJwtHandler;

		if (!(handler instanceof JwtHandler)) {
			handler = new JwtHandler(initialNonce);
			setCachedJwtHandler(handler);
		}

		return handler;
	};

	//# – API Adapter Wrappers

	function reportConversationId(conversationId: string): void {
		const nonce = getJwtHandler().nonce;
		wpFetch("conversation", nonce, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				conversation_id: conversationId,
				page_url: (window.parent.location.href ?? window.location.href).split("#")[0],
			}),
		}).catch((e) => {
			// Silently ignore error. This is non-critical telemetry.
			console.dir(e);
		});
	}

	const fetchThread = async (threadID?: string): Promise<ApiAdapterThread | undefined> => {
		try {
			const result = await apiAdapter?.fetchThread(threadID);

			if (result?.messages.length) {
				result.messages.forEach((message, index) => {
					if (message.role == "assistant") {
						setMessagePostQueue((prevQueue) => [...prevQueue, index]);
					}
				});

				setMessages(result?.messages);
			}

			// Set thread after messages to avoid placeholder flash
			console.log("Thread fetched:");
			console.dir(result);

			setThread(result!.id);
			reportConversationId(result!.id);
			return result;
		} catch (error: any) {
			console.error("Failed to fetch thread: ", error);

			if (error instanceof APIConnectionError) {
				showError("Sorry, the connection failed.");
				setStatusText("Connection failed.");
			} else {
				setThread("");
			}

			return undefined;
		}
	};

	const finalizeMessageSnapshot = async (
		threadID: string,
		messageID: string,
		messagesIndex: number,
	): Promise<void> => {
		const finalMessage = await apiAdapter!.fetchMessage(threadID, messageID);
		finalMessage.content = messageContentFilters.apply(finalMessage.content);
		finalMessage.isStreaming = false;

		setMessages((prevMsgs) => {
			const newMsgs = [...prevMsgs];

			newMsgs[messagesIndex] = finalMessage;

			return newMsgs;
		});
		setMessagePostQueue((prevQueue) => [...prevQueue, messagesIndex]);
	};

	const streamRun = async (threadID: string): Promise<void> => {
		console.log(`streamRun thread ID: ${threadID}`);
		const stream = await apiAdapter!.startStream(threadID);
		streamRef.current = stream;

		stream.on("start", () => {
			streamingMessage.textRef.current = "";
			streamingMessage.indexRef.current = messages.length + 1;
			const streamingMsg = buildMessage("assistant", streamingMessage.textRef.current, {
				isStreaming: true,
			});
			setMessages((prevMsgs) => [...prevMsgs, streamingMsg]);
		});

		stream.on("chunk", (text) => {
			const streamingMsg = buildMessage("assistant", messageDeltaFilters.apply(text), {
				isStreaming: true,
			});
			setMessages((prevMsgs) => {
				const newMsgs = [...prevMsgs];
				newMsgs[streamingMessage.indexRef.current!] = streamingMsg;
				return newMsgs;
			});
			setStatusText(null);
		});

		stream.on("done", (text, messageID): void => {
			setMessages((prevMsgs) => {
				const newMsgs = [...prevMsgs];

				const streamingMsg = buildMessage("assistant", messageContentFilters.apply(text), {
					isStreaming: false,
					source: messageID,
				});

				newMsgs[streamingMessage.indexRef.current!] = streamingMsg;

				return newMsgs;
			});

			console.log(`on done thread ID: ${threadID}`);
			finalizeMessageSnapshot(threadID, messageID, streamingMessage.indexRef.current!);
		});

		stream.on("error", (error) => {
			console.log("Stream error: ");
			console.dir(error);
			showError();
			updateMessage(streamingMessage.indexRef.current!, {
				errorMessage: error.message,
				errorAllowRetry: false,
			});
			cleanupStreaming();
		});

		stream.on("end", () => {
			cleanupStreaming();
		});
	};

	const buildMessage = (
		role: ApiAdapterMessageRole,
		content: string,
		props: object = {},
	): ApiAdapterMessage => ({
		id: "",
		role: role,
		content: content,
		annotations: [],
		...props,
	});

	const extractMessageText = (message: ApiAdapterMessage) =>
		Array.isArray(message.content) ? message.content[0].text.value : message.content;

	const addMessage = (message: ApiAdapterMessage): number => {
		const newIndex = messages.length;
		setMessages((prevMessages) => [...prevMessages, message]);
		return newIndex;
	};

	const updateMessage = (index: number, data: Partial<ApiAdapterMessage>) => {
		setMessages((prevMessages) => {
			if (index < 0 || index >= prevMessages.length) return prevMessages;
			const updated = { ...prevMessages[index], ...data };
			const newMessages = [...prevMessages];
			newMessages[index] = updated;
			return newMessages;
		});
	};

	const removeMessage = (index: number) => {
		setMessages((prevMessages) => {
			const newMessages = [...prevMessages];
			newMessages.splice(index, 1);
			return newMessages;
		});
	};

	const sendMessage = async (text: string): Promise<void> => {
		streamingSpinnerCtrl.open();

		const message = buildMessage("user", text);
		const messageIndex = addMessage(message);

		try {
			let sendThreadID;
			if (thread) {
				sendThreadID = thread;
			} else {
				const newThread = await fetchThread();
				sendThreadID = newThread?.id as string;
			}
			console.log(`sendthread: ${sendThreadID}`);
			await apiAdapter!.createMessage(sendThreadID, message);
			await streamRun(sendThreadID);
			console.log("No sendMessage error.");
		} catch (error: any) {
			console.log("sendMessage error:");
			console.dir(error);

			if (error instanceof AuthenticationError) {
				// Recoverable
				console.log(
					"Authentication error while sending message. Attempting to refresh JWT and retry…",
				);
				const jwtHandler = getJwtHandler();
				await jwtHandler.get();
			} else {
				// Non-recoverable
				let errorMessage: string = "";
				let errorAllowRetry: boolean = true;

				// TODO: Account for APIConnectionError
				if (error instanceof PermissionDeniedError) {
					if (error.message === "403 Access denied") {
						sessionExpiredCtrl.open();
						errorMessage =
							"Your session has expired, or is not valid for this conversation. Please refresh to continue.";
						errorAllowRetry = false;
					} else if (error.message === "403 Prompt blocked") {
						errorMessage =
							"Sorry, this message may not be appropriate for this service. Please try rephrasing or asking a different question.";
						errorAllowRetry = false;
					}
				} else if (error instanceof RateLimitError) {
					errorMessage = "You are sending messages too quickly. Please wait a moment and retry.";
				} else {
					console.error("Uncaught error while sending message: ", error);
					errorMessage = `Sorry, your message did not go through. Please try again. If this error persists, come back later or contact customer support. (Error ${error.message})`;
				}

				updateMessage(messageIndex, {
					errorMessage: errorMessage,
					errorAllowRetry: errorAllowRetry,
				});
				cleanupStreaming();
			}
		}
	};

	const resendMessage = async (index: any): Promise<void> => {
		const message = messages[index];
		const text = extractMessageText(message);
		if (text) {
			setSendQueue((prevQueue) => [...prevQueue, text]);
			removeMessage(index);
		}
	};

	//# – UI Handlers

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
		event.preventDefault();

		const text = userInput.trim();
		if (text !== "") {
			setSendQueue((prevQueue) => [...prevQueue, text]);
			setUserInput("");
		}
	};

	//# – Getters

	const handleCaptcha = async (captcha: string) => {
		await getJwtHandler().get(captcha);

		setCaptchaPending(false);
	};

	const showColorSchemeToggle: boolean = true;

	//# Template

	const views = {
		wait: (
			<ScrollArea className={styles.view}>
				<Group gap="xs" mt="xl" mb="md" justify="center">
					<Loader size="sm" />
					<Text c="dimmed" size="md">
						{statusText}
					</Text>
				</Group>
			</ScrollArea>
		),
		welcome: (
			<ScrollArea className={styles.view}>
				<Box mx="xl" style={{ textAlign: "center" }}>
					{/* <Space h="xl" /> */}
					<SafeHTML className={styles.welcome} content={welcomeHTML} openLinksInNewTab={true} />
					<Space h="xl" />
					{promptSuggestions.length > 0 && (
						<SimpleGrid cols={2} m="md">
							{promptSuggestions.map((prompt, index) => (
								<Text
									key={index}
									className={styles.promptSuggestion}
									size="sm"
									onClick={() => {
										setSendQueue((prevQueue) => [...prevQueue, prompt]);
									}}
								>
									{prompt}
								</Text>
							))}
						</SimpleGrid>
					)}
				</Box>
			</ScrollArea>
		),
		conversation: (
			<AIChatConversationView
				messages={messages}
				statusText={statusText}
				isStreaming={isStreaming}
				resendMessage={resendMessage}
				filterMessage={extractMessageText}
				byColorScheme={byColorScheme}
				utmSource={utmSource}
				utmMedium={utmMedium}
				utmCampaign={utmCampaign}
			/>
		),
	};

	return (
		<Flex className={styles.root} direction="column">
			<AIChatTitlebar
				appTitle={appTitle}
				appTitleImageURL={appTitleImageURL}
				showColorSchemeToggle={showColorSchemeToggle}
				isReady={thread !== undefined && thread !== null}
				setThread={setThread}
			/>
			{views[activeView]}
			<Paper className={styles.footer} pl="md" pr="md" pt="md" radius={0} withBorder>
				<form onSubmit={handleSubmit}>
					<TextInput
						className={styles.input}
						variant="filled"
						size="md"
						placeholder="Type your question"
						value={userInput}
						onChange={(event) => setUserInput(event.currentTarget.value)}
						rightSection={
							<Tooltip label={isStreaming || !apiReady ? "Send [Not Ready]" : "Send"}>
								<ActionIcon
									className={styles.buttonSend}
									variant="filled"
									type="submit"
									disabled={isStreaming || !apiReady}
								>
									<IconSend />
								</ActionIcon>
							</Tooltip>
						}
					/>
				</form>
				<Text c="dimmed" pt="xs" pb="xs" size="sm" ta="center">
					Please remember, like humans, AI can sometimes make mistakes.
				</Text>
			</Paper>
			<Modal
				opened={isSessionExpired || captchaError}
				onClose={sessionExpiredCtrl.close}
				withCloseButton={false}
				centered
			>
				<Center>
					<Text>
						{captchaError ? "There was an error loading the CAPTCHA" : "Your session has expired"}.
						Please refresh to continue.
					</Text>
				</Center>
				<Center>
					<RefreshButton />
				</Center>
			</Modal>
		</Flex>
	);
}
